import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkButton from "../../components/atoms/TkButton";
import TkStepper from "../../components/atoms/TkStepper";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { httpAPI_RET005, httpAPI_STA003 } from "./api/request";
import { fakeTableList, heirStepper, info, retireeStepper } from "./parts";
import "./_style.scss";
import { IAPI_RET005_Request } from "../../type/apiType";
import { DemanderKbn, FormClassRetire } from "../../assets/data/Constants";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import TkDownloadLink from "../../components/atoms/TkDownloadLink";
import _ from "lodash";

/**
 * SCRR411111 職手当金の請求手続き（源泉徴収票の確認）
 * 退職手当金請求者が福祉医療機構以外の退職手当金に係る源泉徴収票を登録する。
 * @return SCRR411111
 */
const SCRR411111 = () => {
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const [stateTableList, setStateTableList] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const rotationData = businessState.job_35 ?? {};
  const isRetiree = rotationData.demanderKbn === DemanderKbn["退職者本人"];
  const step = isRetiree ? 7 : 8;

  const handleInit = async () => {
    try {
      // 退職者情報詳細参照API呼び出し
      const res = await httpAPI_STA003({
        contractorNo: businessState?.common?.contractorNo,
        staffNo: rotationData.staffNo,
        applyId: rotationData.retirementFormApplyId,
      });

      if (res.resultCd === "I0000") {
        let resArr = [...stateFakeTableList.current];

        res.formList?.forEach((item: any) => {
          // 法人が添付した源泉徴収票（1枚目）
          if (
            item["formClass"] ===
            FormClassRetire["退職所得の源泉徴収票_退職届1枚目"]
          ) {
            let corporationAttachedFile1 = resArr.find(
              (item) => item.keyword === "corporationAttachedFile1"
            )!;
            corporationAttachedFile1.showLine = true;
            corporationAttachedFile1.dataname = item.formName;
            corporationAttachedFile1.base64filedata = item.fileBase64;
          }

          // 法人が添付した源泉徴収票（2枚目）
          if (
            item["formClass"] ===
            FormClassRetire["退職所得の源泉徴収票_退職届2枚目"]
          ) {
            let corporationAttachedFile2 = resArr.find(
              (item) => item.keyword === "corporationAttachedFile2"
            )!;
            corporationAttachedFile2.showLine = true;
            corporationAttachedFile2.dataname = item.formName;
            corporationAttachedFile2.base64filedata = item.fileBase64;
          }

          // 法人が添付した源泉徴収票（3枚目）
          if (
            item["formClass"] ===
            FormClassRetire["退職所得の源泉徴収票_退職届3枚目"]
          ) {
            let corporationAttachedFile3 = resArr.find(
              (item) => item.keyword === "corporationAttachedFile3"
            )!;
            corporationAttachedFile3.showLine = true;
            corporationAttachedFile3.dataname = item.formName;
            corporationAttachedFile3.base64filedata = item.fileBase64;
          }
        });

        setStateTableList(resArr);

        setBusinessState({
          ...businessState,
          job_35: {
            ...rotationData,
            tempSaveStep: res.temporarySaveStep,
          },
        });

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = [...stateFakeTableList.current];
    rotationData.fileList?.forEach((item: any) => {
      // 退職者が登録した源泉徴収票（1枚目）
      if (
        item.formClass ===
        FormClassRetire["退職所得の源泉徴収票_手当金請求1枚目"]
      ) {
        let retirementIncomeFile1 = resArr.find(
          (item) => item.keyword === "retirementIncomeFile1"
        )!;
        retirementIncomeFile1.showLine = true;
        retirementIncomeFile1.node = handleSetFileElm(item, "1");
      }

      // 退職者が登録した源泉徴収票（2枚目）
      if (
        item.formClass ===
        FormClassRetire["退職所得の源泉徴収票_手当金請求2枚目"]
      ) {
        let retirementIncomeFile2 = resArr.find(
          (item) => item.keyword === "retirementIncomeFile2"
        )!;
        retirementIncomeFile2.showLine = true;
        retirementIncomeFile2.node = handleSetFileElm(item, "2");
      }
    });
    setStateTableList(resArr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotationData]);

  // 「
  const handleSetFileElm = (item: any, num: string) => {
    return (
      <Box className="link">
        <TkDownloadLink
          dataname={item.formName}
          base64filedata={item.file}
          downloadBase64
        />
        <TkButton onClick={() => handleEdit(num)}>編集</TkButton>
      </Box>
    );
  };

  // 退職者が登録した源泉徴収票の編集ボタン用イベント
  const handleEdit = (editPage: string) => {
    setBusinessState({
      ...businessState,
      job_35: {
        ...rotationData,
        editPage,
      },
    });
    navigate("/SCRR411112");
  };

  const makeHttpObj = () => {
    const reqObj: IAPI_RET005_Request = {
      ...rotationData,
      tempSaveStep: rotationData.demanderKbn === DemanderKbn["相続人"] ? 8 : 7, // 一時保存ステップ
      businessType: businessState.common?.businessType,
      contractorNo:
        state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
          ? businessState?.common?.contractorNo!
          : state.authInfo?.contractorNo!, // 共済契約者番号
      nowZipcode: isRetiree
        ? rotationData.demanderZipcode
        : rotationData.retirementZipcode,
      nowPrefectureCd: isRetiree
        ? rotationData.demanderPrefectureCd
        : rotationData.retirementPrefectureCd,
      nowAddressCity: isRetiree
        ? rotationData.demanderCity
        : rotationData.retirementAddressCity,
      nowAddressOthers: isRetiree
        ? rotationData.demanderAddressOthers
        : rotationData.retirementAddressOthers,
      screenId: "SCRR411111",
    };

    return API_RET005_BuildReqParams(reqObj);
  };

  const handleSubmit = async (_: any, index: any) => {
    switch (index) {
      case 0: // 一時保存
        // 退職手当金請求更新API実行
        try {
          const res = await httpAPI_RET005({
            ...makeHttpObj(),
          });

          if (res.resultCd === "I0000") {
            setState({
              ...state,
              errorMsgId: "",
            });

            setBusinessState({
              ...businessState,
              job_35: {
                ...rotationData,
                tempSaveStep: step,
                applyId: res.applyId,
              },
            });

            setOpen(true);
          } else {
            setState({
              ...state,
              errorMsgId: res.resultCd,
            });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case 1: // 源泉徴収票を追加で添付
        if (rotationData.page2PaymentAmt && rotationData.page2PaymentAmt > 0) {
          setState({
            ...state,
            errorMsgId: "TKMSG_SCR0074E",
          });
        } else if (
          rotationData.page1PaymentAmt &&
          rotationData.page1PaymentAmt > 0
        ) {
          handleEdit("2");
        } else {
          handleEdit("1");
        }
        break;

      case 2: // すべて登録されていることを確認
        navigate("/SCRR411113");
        break;
    }
  };

  return (
    <Box className="SCRR411111" data-testid="SCRR411111PageForTest">
      <TkStepper
        steps={isRetiree ? retireeStepper : heirStepper}
        activeStep={step - 1}
        savaStep={rotationData.tempSaveStep}
      />

      {info}

      {state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO && (
        <TkContractorAndNumberLine
          name="退職者名"
          no={rotationData.staffNameKanji!}
        />
      )}

      <TkFakeTable
        list={stateTableList}
        ruleSchema={[]}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "源泉徴収票を追加で添付" },
          { submitButtonText: "すべて登録されていることを確認" },
        ]}
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={handleSubmit}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        cancelText=""
        okText="はい"
      />
    </Box>
  );
};

export default SCRR411111;
